<template>
  <div class="spine-video-page">
    <!-- 顶部装饰背景 -->
    <div class="page-header">
      <div class="header-decoration">
        <div class="decoration-circle decoration-circle--1"></div>
        <div class="decoration-circle decoration-circle--2"></div>
        <div class="decoration-circle decoration-circle--3"></div>
      </div>
      <div class="header-content">
        <div class="header-icon">
          <svg width="32" height="32" viewBox="0 0 32 32" fill="none">
            <circle cx="16" cy="16" r="14" fill="#4CAF50" opacity="0.2" />
            <path d="M13 10L23 16L13 22V10Z" fill="#4CAF50" />
          </svg>
        </div>
        <h1 class="header-title">脊柱保健操</h1>
        <p class="header-subtitle">每天练一练，脊柱更健康</p>
      </div>
    </div>

    <!-- 下拉刷新 + 内容区域 -->
    <van-pull-refresh v-model="refreshing" @refresh="onRefresh" :head-height="60">
      <!-- 加载状态 -->
      <div v-if="loading && !refreshing" class="loading-wrapper">
      <van-loading type="spinner" color="#4CAF50" size="36px" />
      <p class="loading-text">正在加载精彩内容...</p>
    </div>

    <!-- 空状态 -->
    <div
      v-else-if="!courseList || courseList.length === 0"
      class="empty-wrapper"
    >
      <div class="empty-icon">
        <svg width="80" height="80" viewBox="0 0 80 80" fill="none">
          <circle cx="40" cy="40" r="36" fill="#E8F5E9" />
          <path
            d="M32 28L52 40L32 52V28Z"
            fill="#81C784"
            stroke="#4CAF50"
            stroke-width="2"
          />
        </svg>
      </div>
      <p class="empty-text">暂无视频内容</p>
      <p class="empty-subtext">精彩内容即将上线，敬请期待～</p>
    </div>

    <!-- 视频列表 -->
    <div v-else class="video-list">
      <!-- 顶部提示 -->
      <div class="tips-banner">
        <div class="tips-icon">💡</div>
        <span>点击卡片即可播放视频哦~</span>
      </div>

      <!-- 视频卡片网格 -->
      <div class="video-grid">
        <div
          v-for="(course, index) in courseList"
          :key="course.courseId || index"
          class="video-card"
          :class="getCardColorClass(index)"
          @click="handleVideoClick(course)"
        >
          <!-- 封面图 -->
          <div class="video-card__cover">
            <img
              v-if="course.coverUrl"
              :src="course.coverUrl"
              :alt="course.title"
              class="video-card__image"
            />
            <div v-else class="video-card__placeholder">
              <svg width="40" height="40" viewBox="0 0 40 40" fill="none">
                <circle
                  cx="20"
                  cy="20"
                  r="18"
                  fill="currentColor"
                  opacity="0.1"
                />
                <path
                  d="M16 12L28 20L16 28V12Z"
                  fill="currentColor"
                  opacity="0.6"
                />
              </svg>
            </div>
            <!-- 播放按钮遮罩 -->
            <div class="video-card__play-overlay">
              <div class="play-button">
                <svg width="32" height="32" viewBox="0 0 32 32" fill="none">
                  <circle cx="16" cy="16" r="16" fill="rgba(255,255,255,0.9)" />
                  <path d="M13 10L23 16L13 22V10Z" fill="#4CAF50" />
                </svg>
              </div>
            </div>
            <!-- 时长标签 -->
            <div v-if="course.duration" class="video-card__duration">
              {{ formatDuration(course.duration) }}
            </div>
          </div>

          <!-- 视频信息 -->
          <div class="video-card__info">
            <h3 class="video-card__title">{{ course.title }}</h3>
            <p v-if="course.brief" class="video-card__brief">
              {{ course.brief }}
            </p>
            <div class="video-card__meta">
              <span class="meta-item">
                <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                  <circle
                    cx="7"
                    cy="4"
                    r="3"
                    fill="currentColor"
                    opacity="0.6"
                  />
                  <path
                    d="M1 13C1 10.5 3.5 9 7 9C10.5 9 13 10.5 13 13"
                    stroke="currentColor"
                    stroke-width="1.5"
                    opacity="0.6"
                  />
                </svg>
                2000+人学习
              </span>
            </div>
          </div>

          <!-- 装饰角标 -->
          <div class="video-card__badge" :class="getBadgeColorClass(index)">
            {{ getBadgeText(index) }}
          </div>
        </div>
      </div>
    </div>

    <!-- 底部装饰 -->
    <div class="page-footer">
      <div class="footer-decoration">
        <span class="footer-dot footer-dot--green"></span>
        <span class="footer-dot footer-dot--yellow"></span>
        <span class="footer-dot footer-dot--blue"></span>
        <span class="footer-dot footer-dot--pink"></span>
      </div>
      <p class="footer-text">坚持锻炼，健康成长 🌱</p>
    </div>
    </van-pull-refresh>
  </div>
</template>

<script>
import { PullRefresh, Toast } from 'vant'

export default {
  name: 'SpineExerciseVideoPage',
  components: {
    [PullRefresh.name]: PullRefresh
  },
  data() {
    return {
      loading: true,
      refreshing: false,
      courseList: []
    }
  },
  mounted() {
    this.loadCourseList()
  },
  methods: {
    async loadCourseList() {
      this.loading = true
      try {
        const { data: resp } = await this.$http.get('/course/by_sort', {
          params: {
            courseSort: '29G'
          }
        })

        if (resp && resp.code === 200) {
          this.courseList = resp.data || []
          console.log('课程列表加载成功:', this.courseList)
        } else {
          console.error('加载课程失败:', resp?.message)
          this.courseList = []
        }
      } catch (error) {
        console.error('加载课程异常:', error)
        this.courseList = []
      } finally {
        this.loading = false
      }
    },

    async onRefresh() {
      try {
        const { data: resp } = await this.$http.get('/course/by_sort', {
          params: {
            courseSort: '29G'
          }
        })

        if (resp && resp.code === 200) {
          this.courseList = resp.data || []
          Toast.success('刷新成功')
          console.log('课程列表刷新成功:', this.courseList)
        } else {
          Toast.fail(resp?.message || '刷新失败')
          this.courseList = []
        }
      } catch (error) {
        console.error('刷新课程异常:', error)
        Toast.fail('网络异常，请重试')
        this.courseList = []
      } finally {
        this.refreshing = false
      }
    },

    handleVideoClick(course) {
      if (!course.videoUrl) {
        Toast('视频正在准备中...')
        return
      }

      // 跳转到视频详情页
      this.$router.push({
        path: '/shunyi/video/detail',
        query: {
          id: course.courseId,
          videoUrl: encodeURIComponent(course.videoUrl),
          title: course.title
        }
      })
    },

    formatDuration(milliseconds) {
      if (!milliseconds) return ''
      const totalSeconds = Math.floor(milliseconds / 1000)
      const mins = Math.floor(totalSeconds / 60)
      const secs = totalSeconds % 60
      return `${mins}:${secs.toString().padStart(2, '0')}`
    },

    getCardColorClass(index) {
      const colors = [
        'card--green',
        'card--yellow',
        'card--blue',
        'card--pink',
        'card--orange',
        'card--purple'
      ]
      return colors[index % colors.length]
    },

    getBadgeColorClass(index) {
      const colors = [
        'badge--green',
        'badge--yellow',
        'badge--blue',
        'badge--pink',
        'badge--orange',
        'badge--purple'
      ]
      return colors[index % colors.length]
    },

    getBadgeText(index) {
      const texts = ['推荐', '热门', '精选', '必练', '基础', '进阶']
      return texts[index % texts.length]
    }
  }
}
</script>

<style scoped>
.spine-video-page {
  min-height: 100vh;
  background: linear-gradient(
    180deg,
    #e8f5e9 0%,
    #f1f8e9 30%,
    #fffde7 60%,
    #fff8e1 100%
  );
  padding-bottom: 40px;
}

/* 下拉刷新样式覆盖 */
.spine-video-page ::v-deep .van-pull-refresh {
  overflow: visible;
}

.spine-video-page ::v-deep .van-pull-refresh__track {
  min-height: 200px;
}

.spine-video-page ::v-deep .van-pull-refresh__head {
  display: flex;
  align-items: center;
  justify-content: center;
  color: #4caf50;
}

/* 顶部头部样式 */
.page-header {
  position: relative;
  padding: 40px 20px 30px;
  overflow: hidden;
}

.header-decoration {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  pointer-events: none;
}

.decoration-circle {
  position: absolute;
  border-radius: 50%;
  opacity: 0.3;
}

.decoration-circle--1 {
  width: 120px;
  height: 120px;
  background: linear-gradient(135deg, #81c784 0%, #4caf50 100%);
  top: -40px;
  right: -30px;
}

.decoration-circle--2 {
  width: 80px;
  height: 80px;
  background: linear-gradient(135deg, #ffd54f 0%, #ffc107 100%);
  top: 20px;
  left: -20px;
}

.decoration-circle--3 {
  width: 60px;
  height: 60px;
  background: linear-gradient(135deg, #64b5f6 0%, #2196f3 100%);
  bottom: 10px;
  right: 40px;
}

.header-content {
  position: relative;
  z-index: 1;
  text-align: center;
}

.header-icon {
  margin-bottom: 12px;
}

.header-title {
  font-size: 28px;
  font-weight: 700;
  color: #2e7d32;
  margin: 0 0 8px;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.header-subtitle {
  font-size: 14px;
  color: #558b2f;
  margin: 0;
  letter-spacing: 1px;
}

/* 加载状态 */
.loading-wrapper {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
}

.loading-text {
  margin-top: 16px;
  font-size: 14px;
  color: #66bb6a;
}

/* 空状态 */
.empty-wrapper {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
}

.empty-icon {
  margin-bottom: 20px;
}

.empty-text {
  font-size: 16px;
  color: #666;
  margin: 0 0 8px;
}

.empty-subtext {
  font-size: 13px;
  color: #999;
  margin: 0;
}

/* 提示横幅 */
.tips-banner {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 12px 20px;
  margin: 0 16px 16px;
  background: linear-gradient(135deg, #c8e6c9 0%, #a5d6a7 100%);
  border-radius: 20px;
  font-size: 14px;
  color: #2e7d32;
  font-weight: 500;
}

.tips-icon {
  margin-right: 8px;
  font-size: 18px;
}

/* 视频网格 */
.video-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 16px;
  padding: 0 16px;
}

/* 视频卡片 */
.video-card {
  position: relative;
  background: #ffffff;
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  transition: all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1);
}

.video-card:active {
  transform: scale(0.96);
}

/* 卡片颜色变体 */
.video-card.card--green {
  border-top: 3px solid #4caf50;
}
.video-card.card--yellow {
  border-top: 3px solid #ffc107;
}
.video-card.card--blue {
  border-top: 3px solid #2196f3;
}
.video-card.card--pink {
  border-top: 3px solid #e91e63;
}
.video-card.card--orange {
  border-top: 3px solid #ff9800;
}
.video-card.card--purple {
  border-top: 3px solid #9c27b0;
}

/* 封面区域 */
.video-card__cover {
  position: relative;
  width: 100%;
  aspect-ratio: 16 / 10;
  background: linear-gradient(135deg, #e8f5e9 0%, #c8e6c9 100%);
  overflow: hidden;
}

.video-card__image {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.video-card__placeholder {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #81c784;
}

/* 播放按钮遮罩 */
.video-card__play-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.1);
  opacity: 0;
  transition: opacity 0.3s;
}

.video-card:active .video-card__play-overlay {
  opacity: 1;
}

.play-button {
  transform: scale(0.9);
  transition: transform 0.3s;
}

.video-card:active .play-button {
  transform: scale(1);
}

/* 时长标签 */
.video-card__duration {
  position: absolute;
  right: 8px;
  bottom: 8px;
  padding: 2px 8px;
  background: rgba(0, 0, 0, 0.6);
  border-radius: 10px;
  font-size: 11px;
  color: #fff;
}

/* 视频信息 */
.video-card__info {
  padding: 12px;
}

.video-card__title {
  font-size: 14px;
  font-weight: 600;
  color: #333;
  margin: 0 0 6px;
  line-height: 1.4;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.video-card__brief {
  font-size: 12px;
  color: #888;
  margin: 0 0 8px;
  line-height: 1.4;
  display: -webkit-box;
  -webkit-line-clamp: 1;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.video-card__meta {
  display: flex;
  align-items: center;
  font-size: 11px;
  color: #999;
}

.meta-item {
  display: flex;
  align-items: center;
  gap: 4px;
}

/* 角标 */
.video-card__badge {
  position: absolute;
  top: 8px;
  left: 8px;
  padding: 3px 10px;
  border-radius: 10px;
  font-size: 10px;
  font-weight: 600;
  color: #fff;
}

.badge--green {
  background: linear-gradient(135deg, #66bb6a 0%, #43a047 100%);
}
.badge--yellow {
  background: linear-gradient(135deg, #ffd54f 0%, #ffc107 100%);
  color: #5d4037;
}
.badge--blue {
  background: linear-gradient(135deg, #64b5f6 0%, #2196f3 100%);
}
.badge--pink {
  background: linear-gradient(135deg, #f48fb1 0%, #e91e63 100%);
}
.badge--orange {
  background: linear-gradient(135deg, #ffb74d 0%, #ff9800 100%);
}
.badge--purple {
  background: linear-gradient(135deg, #ba68c8 0%, #9c27b0 100%);
}

/* 底部装饰 */
.page-footer {
  margin-top: 30px;
  text-align: center;
}

.footer-decoration {
  display: flex;
  justify-content: center;
  gap: 12px;
  margin-bottom: 12px;
}

.footer-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
}

.footer-dot--green {
  background: #4caf50;
}
.footer-dot--yellow {
  background: #ffc107;
}
.footer-dot--blue {
  background: #2196f3;
}
.footer-dot--pink {
  background: #e91e63;
}

.footer-text {
  font-size: 13px;
  color: #81c784;
  margin: 0;
}

/* 响应式调整 */
@media (max-width: 360px) {
  .video-grid {
    gap: 12px;
    padding: 0 12px;
  }

  .video-card__info {
    padding: 10px;
  }

  .video-card__title {
    font-size: 13px;
  }
}

@media (min-width: 414px) {
  .header-title {
    font-size: 32px;
  }

  .video-grid {
    gap: 20px;
  }

  .video-card__title {
    font-size: 15px;
  }
}
</style>
